import * as THREE from 'three';
import type { NestConfig } from '../sim/contracts';
import type { Terrain } from '../game/Terrain';

/**
 * Black-hole marker at each nest entrance. An ink-black inner disc ringed by
 * a glowing spiral accretion disc drawn with a custom shader and animated by
 * a time uniform. `update(dt)` ticks the swirl each frame.
 */
export class NestMarker {
  readonly group = new THREE.Group();
  private readonly uniforms = { uTime: { value: 0 } };

  constructor(nest: NestConfig, terrain: Terrain) {
    this.group.name = 'nest-marker';
    const baseY = terrain.getHeight(nest.x, nest.z);

    // Inner event-horizon disc — pure black, slightly raised so it composites
    // cleanly over the crater floor without z-fighting.
    const coreGeo = new THREE.CircleGeometry(nest.radius * 0.55, 48);
    coreGeo.rotateX(-Math.PI / 2);
    const coreMat = new THREE.MeshBasicMaterial({ color: 0x000000, depthWrite: false });
    const core = new THREE.Mesh(coreGeo, coreMat);
    core.position.set(nest.x, baseY + 0.004, nest.z);
    core.renderOrder = 2;
    this.group.add(core);

    // Accretion disc — a ring with a procedural swirl that rotates over time.
    const ringGeo = new THREE.RingGeometry(nest.radius * 0.55, nest.radius * 0.95, 64, 1);
    ringGeo.rotateX(-Math.PI / 2);
    const ringMat = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float uTime;
        varying vec2 vUv;

        void main() {
          // UVs run 0..1 across the ring; centre the radial/angular axes.
          vec2 p = vUv - 0.5;
          float r = length(p) * 2.0;       // 0 at inner rim of the ring quad, ~1 at outer
          float a = atan(p.y, p.x);
          // Logarithmic-ish spiral strands.
          float spiral = sin(a * 6.0 + uTime * 3.2 - r * 8.0);
          float band = smoothstep(0.0, 0.4, spiral) * smoothstep(0.0, 0.3, 1.0 - r);
          // Inner edge hot-white, outer edge deep red.
          vec3 hot = vec3(1.6, 0.9, 0.3);
          vec3 cool = vec3(1.0, 0.15, 0.05);
          vec3 col = mix(cool, hot, smoothstep(0.2, 0.9, 1.0 - r));
          float alpha = band * (0.9 - r * 0.4);
          gl_FragColor = vec4(col * alpha, alpha);
        }
      `,
    });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.position.set(nest.x, baseY + 0.005, nest.z);
    ring.renderOrder = 3;
    this.group.add(ring);
  }

  update(dt: number): void {
    this.uniforms.uTime.value += dt;
  }
}
